// src/hooks/useSsoFlow.ts
import { useCallback, useRef, useState } from "react";

import { api } from "../lib/api";
import { AppError, toUserMessage } from "../lib/errors";

export type SsoLoginResponse = {
  email: string;
  userId?: string;
  secretKey?: string;
  accessToken?: string;
  isNewUser?: boolean;
};

export type SsoFlowPhase = "idle" | "inviteCodeInput";

/**
 * SSO ログイン + 招待コードリトライのフローを管理するフック。
 *
 * 使い方:
 *   const { ssoFlowPhase, startSsoLogin, submitInviteCode, cancelInviteCode, ... } =
 *     useSsoFlow(async (response) => { ... 認証成功後の処理 ... });
 *
 * - startSsoLogin: 任意の SSO プロバイダーのトークンで最初の認証を試みる。
 *   招待コードが必要なら true を返し、内部フェーズを "inviteCodeInput" に遷移させる。
 *   その他のエラーは throw する（呼び出し元でハンドリングすること）。
 * - submitInviteCode: 招待コードを入力して再認証する。エラーは inviteCodeError に格納。
 * - cancelInviteCode: 招待コード入力をキャンセルしてフェーズを "idle" に戻す。
 */
export function useSsoFlow(onSuccess: (response: SsoLoginResponse) => Promise<void>) {
  const [ssoFlowPhase, setSsoFlowPhase] = useState<SsoFlowPhase>("idle");
  const [inviteCodeError, setInviteCodeError] = useState<string | null>(null);
  const [inviteCodeLoading, setInviteCodeLoading] = useState(false);
  const pendingIdTokenRef = useRef<string | null>(null);

  /**
   * SSO トークンで認証を試みる。
   * @returns true: 招待コードが必要（inviteCodeInput フェーズに遷移済み）
   *          false: 認証成功（onSuccess 呼び出し済み）
   * @throws  招待コード以外のエラー（FORBIDDEN、NETWORK 等）
   */
  const startSsoLogin = useCallback(async (idToken: string): Promise<boolean> => {
    try {
      const response = await api.googleSsoLogin({ idToken });
      await onSuccess(response);
      return false;
    } catch (e) {
      if (e instanceof AppError && e.meta.inviteCodeRequired) {
        pendingIdTokenRef.current = idToken;
        setSsoFlowPhase("inviteCodeInput");
        setInviteCodeError(null);
        return true;
      }
      throw e;
    }
  }, [onSuccess]);

  /**
   * 招待コードを付けて認証をリトライする。
   * 403 エラーは inviteCodeError にセット、その他は inviteCodeError にフォールバックメッセージをセット。
   */
  const submitInviteCode = useCallback(async (code: string) => {
    const idToken = pendingIdTokenRef.current;
    if (!idToken) return;

    setInviteCodeError(null);
    setInviteCodeLoading(true);

    try {
      const response = await api.googleSsoLogin({ idToken, invite_code: code.trim() });
      pendingIdTokenRef.current = null;
      setSsoFlowPhase("idle");
      await onSuccess(response);
    } catch (e) {
      if (e instanceof AppError && e.code === "FORBIDDEN") {
        setInviteCodeError("招待コードが正しくありません。もう一度確認してください");
      } else {
        setInviteCodeError(toUserMessage(e));
      }
    } finally {
      setInviteCodeLoading(false);
    }
  }, [onSuccess]);

  /** 招待コード入力をキャンセルしてフェーズを idle に戻す */
  const cancelInviteCode = useCallback(() => {
    pendingIdTokenRef.current = null;
    setSsoFlowPhase("idle");
    setInviteCodeError(null);
  }, []);

  return {
    ssoFlowPhase,
    inviteCodeError,
    inviteCodeLoading,
    startSsoLogin,
    submitInviteCode,
    cancelInviteCode,
  };
}
